import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Switch,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
} from '@mui/material';
import {
  Schedule,
  PlayArrow,
  Refresh,
  History,
  CheckCircle,
  Error as ErrorIcon,
  PauseCircle,
  Memory,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import schedulerService, {
  SchedulerTask,
  SchedulerTaskLog,
} from '../services/schedulerService';

const CATEGORY_LABELS: Record<string, string> = {
  whatsapp: 'WhatsApp',
  payment: 'Pembayaran',
  billing: 'Tagihan',
  reminder: 'Reminder',
  maintenance: 'Maintenance',
};

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default' | 'info'> = {
  success: 'success',
  error: 'error',
  skipped: 'warning',
  running: 'info',
};

const SchedulerManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [logDialog, setLogDialog] = useState<{ open: boolean; task: SchedulerTask | null }>({
    open: false,
    task: null,
  });
  const [logPage, setLogPage] = useState(0);
  const [logStatusFilter, setLogStatusFilter] = useState('');
  const [globalLogStatus, setGlobalLogStatus] = useState('');
  const [globalLogTask, setGlobalLogTask] = useState('');
  const [globalLogPage, setGlobalLogPage] = useState(0);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: status, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['scheduler-status'],
    queryFn: () => schedulerService.getStatus(),
    refetchInterval: 15000,
  });

  const { data: taskLogs, isLoading: logsLoading } = useQuery({
    queryKey: ['scheduler-task-logs', logDialog.task?.task_key, logPage, logStatusFilter],
    queryFn: () =>
      schedulerService.getTaskLogs(logDialog.task!.task_key, {
        page: logPage + 1,
        limit: 20,
        status: logStatusFilter || undefined,
      }),
    enabled: logDialog.open && !!logDialog.task,
  });

  const { data: globalLogs, isLoading: globalLogsLoading } = useQuery({
    queryKey: ['scheduler-global-logs', globalLogPage, globalLogStatus, globalLogTask],
    queryFn: () =>
      schedulerService.getLogs({
        page: globalLogPage + 1,
        limit: 25,
        status: globalLogStatus || undefined,
        task_key: globalLogTask || undefined,
      }),
    refetchInterval: 30000,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ taskKey, enabled }: { taskKey: string; enabled: boolean }) =>
      schedulerService.toggleTask(taskKey, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduler-status'] });
      setMessage({ type: 'success', text: 'Status task berhasil diperbarui' });
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Gagal memperbarui task' });
    },
  });

  const runMutation = useMutation({
    mutationFn: (taskKey: string) => schedulerService.runTask(taskKey),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['scheduler-status'] });
      queryClient.invalidateQueries({ queryKey: ['scheduler-global-logs'] });
      setMessage({ type: 'success', text: res.message || 'Task berhasil dijalankan' });
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Gagal menjalankan task' });
    },
  });

  const formatDuration = (ms?: number) => {
    if (!ms && ms !== 0) return '-';
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(1)} d`;
  };

  const formatDateTime = (value?: string) => {
    if (!value) return '-';
    return format(new Date(value), 'dd MMM yyyy HH:mm:ss', { locale: idLocale });
  };

  const getTaskName = (taskKey: string) =>
    status?.tasks.find((t) => t.task_key === taskKey)?.name || taskKey;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Gagal memuat data scheduler. Pastikan migration sudah dijalankan.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Schedule color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" fontWeight={700}>
              Scheduler & Monitoring
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Kelola task terjadwal, pantau status proses, dan lihat log eksekusi
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => refetch()}
          disabled={isFetching}
        >
          Refresh
        </Button>
      </Stack>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Memory />
              <Typography variant="h6">Status Proses</Typography>
            </Stack>
            <Chip
              label={status?.is_online ? 'ONLINE' : 'OFFLINE'}
              color={status?.is_online ? 'success' : 'error'}
              sx={{ mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              Host: {status?.heartbeat?.hostname || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              PID: {status?.heartbeat?.pid || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mulai: {formatDateTime(status?.heartbeat?.started_at)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Heartbeat: {formatDateTime(status?.heartbeat?.last_heartbeat_at)}
            </Typography>
            {!status?.is_online && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Scheduler tidak terdeteksi. Jalankan: npm run start:scheduler
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Ringkasan
            </Typography>
            <Typography variant="h4">{status?.stats.enabled_tasks}/{status?.stats.total_tasks}</Typography>
            <Typography variant="body2" color="text.secondary">
              Task aktif
            </Typography>
            <Chip
              icon={<ErrorIcon />}
              label={`${status?.stats.errors_24h || 0} error (24 jam)`}
              color={(status?.stats.errors_24h || 0) > 0 ? 'error' : 'default'}
              size="small"
              sx={{ mt: 1 }}
            />
          </CardContent>
        </Card>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Daftar Task Terjadwal
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Task</TableCell>
                  <TableCell>Jadwal</TableCell>
                  <TableCell>Kategori</TableCell>
                  <TableCell align="center">Aktif</TableCell>
                  <TableCell>Terakhir jalan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Durasi</TableCell>
                  <TableCell>Pesan terakhir</TableCell>
                  <TableCell align="center">Run / Error</TableCell>
                  <TableCell align="center">Aksi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {status?.tasks.map((task) => (
                  <TableRow key={task.task_key} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {task.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {task.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={task.schedule_label} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {CATEGORY_LABELS[task.category] || task.category}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={task.is_enabled}
                        onChange={(e) =>
                          toggleMutation.mutate({
                            taskKey: task.task_key,
                            enabled: e.target.checked,
                          })
                        }
                        disabled={toggleMutation.isPending}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDateTime(task.last_run_at)}</TableCell>
                    <TableCell>
                      {task.last_status ? (
                        <Chip
                          label={task.last_status.toUpperCase()}
                          size="small"
                          color={STATUS_COLORS[task.last_status] || 'default'}
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{formatDuration(task.last_duration_ms)}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography variant="body2" noWrap title={task.last_message || ''}>
                        {task.last_message || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {task.run_count} / {task.error_count}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Jalankan sekarang">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => runMutation.mutate(task.task_key)}
                              disabled={runMutation.isPending}
                            >
                              <PlayArrow fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Lihat log">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setLogPage(0);
                              setLogStatusFilter('');
                              setLogDialog({ open: true, task });
                            }}
                          >
                            <History fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Log Eksekusi Terbaru
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Filter task</InputLabel>
              <Select
                value={globalLogTask}
                label="Filter task"
                onChange={(e) => {
                  setGlobalLogTask(e.target.value);
                  setGlobalLogPage(0);
                }}
              >
                <MenuItem value="">Semua task</MenuItem>
                {status?.tasks.map((t) => (
                  <MenuItem key={t.task_key} value={t.task_key}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={globalLogStatus}
                label="Status"
                onChange={(e) => {
                  setGlobalLogStatus(e.target.value);
                  setGlobalLogPage(0);
                }}
              >
                <MenuItem value="">Semua</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="error">Error</MenuItem>
                <MenuItem value="skipped">Skipped</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {globalLogsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Waktu</TableCell>
                      <TableCell>Task</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Trigger</TableCell>
                      <TableCell>Durasi</TableCell>
                      <TableCell>Pesan</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {globalLogs?.logs?.length ? (
                      globalLogs.logs.map((log: SchedulerTaskLog) => (
                        <TableRow key={log.id} hover>
                          <TableCell>{formatDateTime(log.started_at)}</TableCell>
                          <TableCell>{getTaskName(log.task_key)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={log.status}
                              color={STATUS_COLORS[log.status] || 'default'}
                              icon={
                                log.status === 'success' ? (
                                  <CheckCircle />
                                ) : log.status === 'error' ? (
                                  <ErrorIcon />
                                ) : (
                                  <PauseCircle />
                                )
                              }
                            />
                          </TableCell>
                          <TableCell>{log.trigger_type}</TableCell>
                          <TableCell>{formatDuration(log.duration_ms)}</TableCell>
                          <TableCell sx={{ maxWidth: 320 }}>
                            <Typography variant="body2" noWrap title={log.message || ''}>
                              {log.message || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          Belum ada log eksekusi
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={globalLogs?.pagination?.total || 0}
                page={globalLogPage}
                onPageChange={(_, p) => setGlobalLogPage(p)}
                rowsPerPage={25}
                rowsPerPageOptions={[25]}
              />
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={logDialog.open}
        onClose={() => setLogDialog({ open: false, task: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Log: {logDialog.task?.name}</DialogTitle>
        <DialogContent dividers>
          <FormControl size="small" sx={{ mb: 2, minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={logStatusFilter}
              label="Status"
              onChange={(e) => {
                setLogStatusFilter(e.target.value);
                setLogPage(0);
              }}
            >
              <MenuItem value="">Semua</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </Select>
          </FormControl>

          {logsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Waktu</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Trigger</TableCell>
                    <TableCell>Durasi</TableCell>
                    <TableCell>Pesan</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {taskLogs?.logs?.map((log: SchedulerTaskLog) => (
                    <TableRow key={log.id}>
                      <TableCell>{formatDateTime(log.started_at)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={log.status}
                          color={STATUS_COLORS[log.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell>{log.trigger_type}</TableCell>
                      <TableCell>{formatDuration(log.duration_ms)}</TableCell>
                      <TableCell>{log.message || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            count={taskLogs?.pagination?.total || 0}
            page={logPage}
            onPageChange={(_, p) => setLogPage(p)}
            rowsPerPage={20}
            rowsPerPageOptions={[20]}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogDialog({ open: false, task: null })}>Tutup</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchedulerManagement;
